import React, { useState, useEffect } from 'react';
import { FaHourglassHalf } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import axios from 'axios';

const RasieApprovedVoice = () => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvedCount, setApprovedCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(voices.length / rowsPerPage);

  useEffect(() => {
    const fetchApprovedVoices = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get/approved/ryv/post`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const data = response.data.data || response.data;
        const approvedVoices = Array.isArray(data) ? data.filter(voice => voice.status === 'approved') : [];
        setVoices(approvedVoices);
        setApprovedCount(approvedVoices.length);
      } catch (error) {
        toast.error('Failed to load approved voices');
      } finally {
        setLoading(false);
      }
    };
    fetchApprovedVoices();
  }, []);

  // Pagination handler
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  return (
    <div className="p-6">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Approved Raise Voices</h1>
        <div className="flex flex-wrap gap-2">
          <Link to="/raise/underreviewvoice" className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600">Under Review voice</Link>
          <Link to="/raise/runningvoice" className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">Running Voices</Link>
          <Link to="/raise/rejectedvoice" className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">Rejected Voices</Link>
        </div>
      </div>

      {/* Stats Card */}
      <div className="mb-8">
        <div className="bg-yellow-500 p-6 rounded-xl shadow-lg text-white">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium opacity-80">Approved Voices</p>
              <p className="text-2xl font-bold mt-1">{approvedCount}</p>
            </div>
            <FaHourglassHalf className="w-10 h-10 opacity-80" />
          </div>
        </div>
      </div>

      {/* Voices Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      ) : voices.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <FaHourglassHalf className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Approved Voices</h3>
          <p className="text-gray-500">You don't have any approved voices at the moment.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl shadow-sm border border-gray-100">
              <thead>
                <tr className="bg-yellow-100 text-yellow-800">
                  <th className="py-3 px-4 text-left">Media</th>
                  <th className="py-3 px-4 text-left">RYV ID</th>
                  <th className="py-3 px-4 text-left">User ID</th>
                  <th className="py-3 px-4 text-left">Location (State, City)</th>
                  <th className="py-3 px-4 text-left">Residence Address</th>
                  <th className="py-3 px-4 text-left">Description</th>
                  <th className="py-3 px-4 text-left">Created At</th>
                  <th className="py-3 px-4 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {[...voices]
                  .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
                  .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                  .map((voice) => (
                    <tr key={voice._id || voice.id} className="border-t">
                      <td className="py-3 px-4">
                        {voice.image && (
                          <img
                            src={voice.image.startsWith('http') ? voice.image : `${process.env.REACT_APP_API_BASE_URL}/${voice.image.replace(/\\/g, '/')}`}
                            alt={voice.title || 'media'}
                            className="w-20 h-12 object-contain bg-gray-100 rounded mb-1"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        )}
                        {voice.video && (
                          <video
                            src={voice.video.startsWith('http') ? voice.video : `${process.env.REACT_APP_API_BASE_URL}/${voice.video.replace(/\\/g, '/')}`}
                            controls
                            className="w-20 h-12 object-contain bg-black rounded"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        )}
                        {!voice.image && !voice.video && <span className="text-gray-400">No media</span>}
                      </td>
                      <td className="py-3 px-4 font-semibold">{voice._id}</td>
                      <td className="py-3 px-4">{voice.userId || <span className="text-gray-400">N/A</span>}</td>
                      <td className="py-3 px-4">{`${voice.state || 'N/A'}, ${voice.city || 'N/A'}`}</td>
                      <td className="py-3 px-4">{voice.residenceAddress || voice.address || <span className="text-gray-400">N/A</span>}</td>
                      <td className="py-3 px-4">{voice.description || <span className="text-gray-400">N/A</span>}</td>
                      <td className="px-4 py-2">
                        {(() => {
                          const d = new Date(voice.createdAt);
                          const date = d.toLocaleDateString("en-GB"); // dd/mm/yyyy
                          const time = d.toLocaleTimeString("en-GB", {
                            hour12: true,
                          }); // hh:mm:ss
                          return `${date} ${time}`;
                        })()}
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center w-max">
                          Approved
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <nav className="inline-flex flex-wrap items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Previous
                </button>
                {/* Page Numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors font-semibold ${currentPage === page ? 'bg-yellow-300 text-yellow-900 border-yellow-600' : ''}`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RasieApprovedVoice;
